/**
 * Generates a new fingering string based on the dragged position.
 * @param {number} xChange - The change in the x-coordinate.
 * @param {number} yChange - The change in the y-coordinate.
 * @param {string | null} originalFingerStr - The original fingering string.
 * @returns {string} The new fingering string.
 */
const getDraggedFingering = (
  xChange: number,
  yChange: number,
  originalFingerStr: string | null
): string => {
  const pxPerStep = 50;
  //oringinal finger is a string, number with dashes, for example "2-3-5"
  //get the list of numbers from the string, and their index in the original string
  const numList: number[] = [];
  const indexList: number[] = [];
  if (originalFingerStr) {
    let currentIndex = 0;
    const parts = originalFingerStr.split('-');
    for (const part of parts) {
      numList.push(Number(part));
      indexList.push(currentIndex);
      currentIndex += part.length + 1; // +1 for the dash
    }
  }

  // get the index of the number that is being dragged
  const zeroX = -0.5 * pxPerStep * numList.length;
  let draggedIndex = Math.floor((xChange - zeroX) / pxPerStep);
  if (draggedIndex < 0) {
    draggedIndex = 0;
  } else if (draggedIndex >= numList.length) {
    draggedIndex = numList.length - 1;
  }

  // update the number that is being dragged
  const originalFingerNum = numList[draggedIndex];
  // devide by pxPerStep and divide by 6 to get the remainder
  let fingering = (originalFingerNum - 1 - Math.round(yChange / pxPerStep)) % 5;
  // if the remainder is negative, add 5 to it
  if (fingering < 0) {
    fingering += 5;
  }
  //get the new fingering string
  let newFingerStr = '';
  for (let i = 0; i < numList.length; i++) {
    if (i === draggedIndex) {
      newFingerStr += fingering + 1;
    } else {
      newFingerStr += numList[i];
    }
    if (i < numList.length - 1) {
      newFingerStr += '-';
    }
  }

  return newFingerStr;
};

// Class name used by the inner number span inside the drag helper.
// Exported so the drag-move handler can rewrite ONLY the number's
// text without wiping the surrounding triangle hint elements.
const DRAG_HELPER_NUMBER_CLASS = 'temp-letter-number';

// Visual style of the up/down hint triangles. Flat (no stroke,
// solid fill) so they read as direction cues rather than icons —
// same colour as the magnified number with a hint of transparency
// so they don't fight the digit for attention.
const TRIANGLE_BASE_W = 10; // px — half-width of each triangle
const TRIANGLE_HEIGHT = 8; // px — height of each triangle
const TRIANGLE_COLOR = '#6086cc';
const TRIANGLE_OPACITY = '0.7';

/** Build a flat solid triangle using the classic CSS border trick. */
const createHintTriangle = (direction: 'up' | 'down'): HTMLDivElement => {
  const tri = document.createElement('div');
  tri.style.width = '0';
  tri.style.height = '0';
  tri.style.borderLeft = `${TRIANGLE_BASE_W}px solid transparent`;
  tri.style.borderRight = `${TRIANGLE_BASE_W}px solid transparent`;
  if (direction === 'up') {
    tri.style.borderBottom = `${TRIANGLE_HEIGHT}px solid ${TRIANGLE_COLOR}`;
  } else {
    tri.style.borderTop = `${TRIANGLE_HEIGHT}px solid ${TRIANGLE_COLOR}`;
  }
  tri.style.opacity = TRIANGLE_OPACITY;
  return tri;
};

/**
 * Creates the drag-feedback helper — a magnified copy of the
 * fingering number, sandwiched between two flat triangles that hint
 * "drag up or down to change the value". Returned as a single
 * `<div>` container so callers can move the whole stack as one unit.
 *
 * Layout (top → bottom):
 *
 *     ▲   <- "drag up to increase" hint triangle
 *     5   <- magnified, centred number (the live drag value)
 *     ▼   <- "drag down to decrease" hint triangle
 *
 * Drag-move callers rewrite the inner `.temp-letter-number` span's
 * text rather than the container's `innerText` so the two triangles
 * don't get wiped on every pointer move.
 */
const createLetterElement = (
  position: { pageX: number; pageY: number },
  originalFingering: string
): HTMLDivElement => {
  const container = document.createElement('div');
  container.id = 'temp-letter-a';
  container.style.position = 'absolute';
  // Use translate to centre the whole stack horizontally on the
  // cursor regardless of how wide the number string is — handles
  // single digits and multi-digit groups like "2-3-5" alike.
  container.style.left = `${position.pageX}px`;
  container.style.top = `${position.pageY - 60}px`;
  container.style.transform = 'translate(-50%, 0)';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.alignItems = 'center';
  container.style.gap = '4px';
  container.style.pointerEvents = 'none';
  // The drag helper is a pure visual; it should never start a text
  // selection or block the cursor underneath.
  container.style.userSelect = 'none';
  // Lift above the score so it can never be hidden behind the SVG
  // overlay or surrounding chrome during a drag.
  container.style.zIndex = '9999';

  const numberSpan = document.createElement('span');
  numberSpan.className = DRAG_HELPER_NUMBER_CLASS;
  numberSpan.style.fontSize = '32px';
  numberSpan.style.fontWeight = 'bold';
  numberSpan.style.color = '#6086cc';
  numberSpan.style.lineHeight = '1';
  numberSpan.innerText = originalFingering || '';

  container.appendChild(createHintTriangle('up'));
  container.appendChild(numberSpan);
  container.appendChild(createHintTriangle('down'));
  document.body.appendChild(container);
  return container;
};

/**
 * Updates only the magnified-number text inside a drag helper
 * created by `createLetterElement`, leaving the surrounding hint
 * triangles intact. Use this in the pointer-move handler instead
 * of writing to `letterElement.innerText` (which would replace
 * every child of the container — including the triangles).
 */
const setDragHelperText = (letterElement: HTMLElement, text: string): void => {
  const numberSpan = letterElement.querySelector<HTMLElement>(
    `.${DRAG_HELPER_NUMBER_CLASS}`
  );
  if (numberSpan) numberSpan.innerText = text;
};

/**
 * Returns the current value displayed inside a drag helper. The
 * drag-end handler uses this to read the committed value without
 * picking up stray text from the triangle siblings.
 */
const getDragHelperText = (letterElement: HTMLElement): string => {
  const numberSpan = letterElement.querySelector<HTMLElement>(
    `.${DRAG_HELPER_NUMBER_CLASS}`
  );
  return numberSpan?.innerText ?? '';
};

/**
 * Creates vertical dashed line elements centered at the given position.
 * @param {Object} position - The position object containing pageX and pageY.
 * @param {number} numLength - The number of lines to create.
 */
const createLineElements = (
  position: { pageX: number; pageY: number },
  numLength: number
) => {
  for (let i = 1; i < numLength; i++) {
    const lineElement = document.createElement('div');
    lineElement.className = 'temp-line'; // Assign class name for easy removal
    lineElement.style.position = 'absolute';
    lineElement.style.top = `${position.pageY - 100}px`;
    lineElement.style.left = `${position.pageX + (i - numLength / 2) * 50}px`;
    lineElement.style.width = '1px';
    lineElement.style.height = '200px';
    lineElement.style.borderLeft = '1px dashed #6086cc';
    document.body.appendChild(lineElement);
  }
};

/**
 * Removes all elements with the specified class name from the document.
 * @param {string} className - The class name of the elements to remove.
 */
const removeElementsByClassName = (className: string) => {
  const elements = document.getElementsByClassName(className);
  while (elements.length > 0) {
    elements[0].remove();
  }
};

export {
  getDraggedFingering,
  createLetterElement,
  setDragHelperText,
  getDragHelperText,
  createLineElements,
  removeElementsByClassName,
};
